// Tenantra Inc. - Unit grid inside the property slide-out: who is in which unit, add or remove units
import { useEffect, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { navigate } from '@/services/navigation';
import api, { apiHelpers } from '@/services/api';
import { generateUnits, MAX_UNITS } from '@/components/landlord/UnitsBuilder';
import { Section } from './DetailPanel';

const TONE = {
  occupied: 'border-slate-200 bg-white',
  pending: 'border-amber-200 bg-amber-50/50',
  onboarding: 'border-teal-200 bg-teal-50/40',
  vacant: 'border-dashed border-slate-300 bg-white',
};
const WORD = { occupied: null, pending: 'Pending consent', onboarding: 'Onboarding', vacant: 'Vacant' };

function UnitTile({ u, onRemove, propertyId }) {
  const open = u.tenant_id ? () => navigate(`/landlord/tenants/${u.tenant_id}`) : u.application_id ? () => navigate(`/landlord/screening?property=${propertyId}&open=${u.application_id}`) : undefined;
  return (
    <div onClick={open} className={`group relative rounded-md border px-2.5 py-2 min-w-0 ${TONE[u.status]} ${open ? 'cursor-pointer hover:border-teal-300' : ''}`} data-testid={`unit-tile-${u.number}`}>
      <p className="text-[11px] text-slate-400 tabular-nums">Unit {u.number}</p>
      <p className={`text-xs truncate ${u.status === 'vacant' ? 'text-slate-500' : 'text-slate-800 font-medium'}`} data-testid={`unit-tile-${u.number}-occupant`}>
        {u.tenant_name || WORD[u.status]}{u.household_extra > 0 && <span className="text-slate-400 font-normal"> +{u.household_extra}</span>}
      </p>
      {u.tenant_name && WORD[u.status] && <p className={`text-[10px] ${u.status === 'onboarding' ? 'text-teal-700' : 'text-amber-700'}`}>{WORD[u.status]}</p>}
      {u.roommate_id_pending > 0 && <p className="text-[10px] text-amber-700" data-testid={`unit-tile-${u.number}-roommate-pending`}>Roommate ID pending</p>}
      {u.status === 'vacant' && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(u); }} aria-label={`Remove unit ${u.number}`} className="absolute top-1 right-1 w-5 h-5 rounded inline-flex sm:hidden sm:group-hover:inline-flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`unit-tile-remove-${u.number}`}><X className="w-3 h-3" /></button>
      )}
    </div>
  );
}

function AddUnitsForm({ existing, onAdd, onCancel }) {
  const nums = existing.map(u => parseInt(u.number, 10)).filter(n => !Number.isNaN(n));
  const [count, setCount] = useState('1');
  const [start, setStart] = useState(String(nums.length ? Math.max(...nums) + 1 : 1));
  const [busy, setBusy] = useState(false);
  const list = generateUnits(count, start);
  const submit = async () => { setBusy(true); try { await onAdd(list); } finally { setBusy(false); } };
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/60" data-testid="add-units-form">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] text-slate-500">How many<Input value={count} inputMode="numeric" onChange={(e) => setCount(e.target.value.replace(/\D/g, '').slice(0, 3))} className="h-8 w-20 text-sm mt-0.5 bg-white" data-testid="add-units-count" /></label>
        <label className="text-[11px] text-slate-500">Starting at<Input value={start} inputMode="numeric" onChange={(e) => setStart(e.target.value.replace(/\D/g, '').slice(0, 6))} className="h-8 w-24 text-sm mt-0.5 bg-white" data-testid="add-units-start" /></label>
        <button onClick={submit} disabled={!list.length || busy || existing.length + list.length > MAX_UNITS} className="h-8 px-3 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1" data-testid="add-units-submit">{busy && <Loader2 className="w-3 h-3 animate-spin" />} Add {list.length > 1 ? `${list[0]} to ${list[list.length - 1]}` : list[0] ? `unit ${list[0]}` : ''}</button>
        <button onClick={onCancel} className="h-8 px-2 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

export function PropertyUnits({ propertyId, onUnitsChange }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get(`/properties/${propertyId}/units`).then(r => setData(r.data)).catch(e => toast.error(apiHelpers.handleError(e)));
  useEffect(() => { setData(null); setAdding(false); load(); }, [propertyId]); // eslint-disable-line

  const add = async (numbers) => {
    try { const r = await api.post(`/properties/${propertyId}/units`, { numbers }); onUnitsChange?.(r.data.units); toast.success(`${numbers.length} unit${numbers.length === 1 ? '' : 's'} added`); setAdding(false); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove unit ${u.number}?`)) return;
    try { const r = await api.delete(`/properties/${propertyId}/units/${u.id}`); onUnitsChange?.(r.data.units); toast.success(`Unit ${u.number} removed`); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); }
  };

  return (
    <Section title="Units" aside={data ? `${data.occupied} of ${data.total} occupied` : ''}>
      {!data ? <div className="h-16 bg-slate-100 rounded animate-pulse" /> : (
        <div className="space-y-3" data-testid="panel-property-units">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-80 overflow-y-auto">
            {data.items.map(u => <UnitTile key={u.id} u={u} onRemove={remove} propertyId={propertyId} />)}
          </div>
          {adding ? <AddUnitsForm existing={data.items} onAdd={add} onCancel={() => setAdding(false)} />
            : <button onClick={() => setAdding(true)} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="panel-add-units"><Plus className="w-3.5 h-3.5" /> Add units</button>}
        </div>
      )}
    </Section>
  );
}
